import React, { useState } from 'react';
import { Flex, Gutter, Title, GridBag, BodyText } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';

interface NameProps {
  title: string;
  name: string | undefined;
  nameTranslations: { localeId: number; translation: string }[] | undefined;
  locale: LocaleResponse;
  financeId: string | undefined;
  handleNameChange: (name: string) => void;
  handleNameTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  handleFinanceIdChange: (financeId: string) => void;
  nameError: Errors['name'];
  financeIdError: Errors['financeId'];
}

function Name(props: NameProps) {
  const {
    title,
    name,
    nameTranslations,
    locale,
    financeId,
    handleNameChange,
    handleNameTranslationsChange,
    handleFinanceIdChange,
    nameError,
    financeIdError,
  } = props;
  const { t } = useTranslation('bundlescreen');

  const [nameReferenceInputOpen, setNameReferenceInputOpen] = useState<boolean>(false);

  const handleNameTranslationChange = (nameTranslation: string) => {
    const index = (nameTranslations || []).findIndex(translation => translation.localeId === locale.id);
    if (index !== -1 && nameTranslations) {
      const updatedTranslation = { ...nameTranslations[index], translation: nameTranslation };
      const updatedTranslations = updateEntityByIndex(nameTranslations, index, updatedTranslation);
      handleNameTranslationsChange(updatedTranslations);
    }
  };

  const currentNameTranslation = nameTranslations?.find(translation => translation.localeId === locale.id)?.translation;

  return (
    <Flex directionColumn>
      <Title size="md" padding={{ right: 'lg' }}>
        {title}
      </Title>
      <Gutter />
      <BodyText color="gray400">{t('name_section.info')}</BodyText>
      <Gutter />

      <GridBag>
        <GridBag.Item md={6}>
          <EditableItem
            backgroundHoverColor="gray200"
            backgroundColor="gray100"
            divProps={{
              padding: { size: 'sm' },
            }}
          >
            <EditableItem.Input
              value={currentNameTranslation}
              edit={true}
              inputProps={{
                name: 'nameTranslation',
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleNameTranslationChange(e.target.value),
                placeholder: t('name_section.name_translation_placeholder', { language: locale.name.split(' ')[0] }),
                label: t('name_section.name_translation_label'),
                onKeyDown: () => undefined,
              }}
            />
            <Gutter size="sm" />
            <EditableItem.Input
              value={name}
              edit={nameReferenceInputOpen}
              isReference={true}
              focusOnEdit={true}
              inputProps={{
                name: 'name',
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  handleNameChange(e.target.value);
                  setNameReferenceInputOpen(false);
                },
                placeholder: t('name_section.name_reference_placeholder'),
                label: t('name_section.name_reference_label'),
                onKeyDown: () => undefined,
                required: true,
                errorText: nameError,
              }}
              editIconProps={{
                'data-testid': `name-edit`,
                onClick: () => setNameReferenceInputOpen(true),
              }}
            />
          </EditableItem>
        </GridBag.Item>
        <GridBag.Item md={6}>
          <EditableItem
            backgroundHoverColor="gray200"
            backgroundColor="gray100"
            divProps={{
              padding: { size: 'sm' },
            }}
          >
            <EditableItem.Input
              value={financeId}
              edit={true}
              inputProps={{
                name: 'financeId',
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleFinanceIdChange(e.target.value),
                required: true,
                placeholder: t('name_section.finance_id_placeholder'),
                label: t('name_section.finance_id_label'),
                onKeyDown: () => undefined,
                errorText: financeIdError,
              }}
            />
          </EditableItem>
        </GridBag.Item>
      </GridBag>
    </Flex>
  );
}

export default React.memo(Name);
